/* eslint-disable react-refresh/only-export-components -- this hook module intentionally co-locates lazy-loaded modal components with the useClientActionsMenu hook; splitting them is out of scope */
import ConfirmationDialog from '@/components/common/ConfirmationDialog';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { AdvisorInvestor } from '@/store/usersApi';
import {
  useDeleteAdvisorInvestorMutation,
  useLazyGetAdvisorInvestorUserProfileQuery,
  useSendPasswordResetEmailMutation,
} from '@/store/usersApi';
import { exportUserProfilePdf } from '@/utils/exportUserProfilePdf';
import {
  isInviteEligible,
  isDeleteEligible,
  isInvitationHistoryEligible,
} from '@/modules/clientServices/utils/clientActionEligibility';
import { OnboardingStepNumber } from '@/utils/enums';
import { useModuleAccess } from '@/permissions';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { IconButtonStyledOutlined } from '@/components/common/iconButtonStyledOutlined';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { useSelector } from 'react-redux';
import type { WARootState } from '@/store/store';
import ViewUserProfileModal from '@/modules/clientServices/ViewUserProfileModal';
import { useAccessInvestor } from '@/hooks/useAccessInvestor';

const EditInvestorFormFlyout = React.lazy(
  async () =>
    import('@/components/flyouts/ClientDetailsFlyout/EditInvestorFormFlyout'),
);

const InvitationHistory = React.lazy(
  async () =>
    import('@/components/flyouts/ClientDetailsFlyout/InvitationHistory'),
);

const ClientInviteModal = React.lazy(
  async () => import('@/modules/clientServices/ClientInviteModal'),
);

const AddSpousePartnerModal = React.lazy(
  async () =>
    import('@/components/flyouts/ClientDetailsFlyout/AddSpousePartnerModal'),
);

export type ClientViewMode =
  | 'view'
  | 'edit'
  | 'inviteHistory'
  | 'accessInvestor';

export type ClientActionKey =
  | 'inviteUser'
  | 'invitationHistory'
  | 'sendPasswordReset'
  | 'editUserInformation'
  | 'addSpouseOrPartner'
  | 'deleteUser'
  | 'viewUserProfile'
  | 'exportUserProfile'
  | 'accessInvestor';

type UseClientActionsMenuOptions = {
  onDeleted?: (investor: AdvisorInvestor) => void;
  invitationHistoryVariant?: 'inline' | 'dialog';
  // When provided, this is called instead of switching to inline edit view
  onEditUser?: (investor: AdvisorInvestor) => void;
  hideActions?: ReadonlyArray<ClientActionKey>;
};

type MenuForInvestorProps = {
  investor: AdvisorInvestor;
  className?: string;
  size?: 'small' | 'medium';
  hideTrigger?: boolean;
};

type UseClientActionsMenuResult = {
  MenuForInvestor: React.FC<MenuForInvestorProps>;
  viewMode: ClientViewMode;
  Accompaniment: React.ReactNode | null;
  setViewMode: (mode: ClientViewMode, investor?: AdvisorInvestor) => void;
  openMenuForInvestor: (
    investor: AdvisorInvestor,
    event: React.MouseEvent<HTMLElement>,
  ) => void;
  selectedInvestor: AdvisorInvestor | null;
  impersonationError: string | null;
  clearImpersonationError: () => void;
};

export const useClientActionsMenu = (
  options?: UseClientActionsMenuOptions,
): UseClientActionsMenuResult => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentRole = useSelector((s: WARootState) => s.auth.currentRole);
  const { validateByModule, validateByCode, validateByFeature } =
    useModuleAccess();

  const [viewModeState, setViewModeState] = useState<ClientViewMode>('view');
  const [selectedInvestor, setSelectedInvestor] =
    useState<AdvisorInvestor | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [inviteModalOpen, setInviteModalOpen] = useState<boolean>(false);
  const [inviteHistoryDialogOpen, setInviteHistoryDialogOpen] =
    useState<boolean>(false);
  const [addSpouseOpen, setAddSpouseOpen] = useState<boolean>(false);
  const [viewProfileOpen, setViewProfileOpen] = useState<boolean>(false);
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] =
    useState<boolean>(false);

  const returnToView = useCallback(() => {
    setViewModeState('view');
  }, []);
  const {
    startAccess,
    resetAccess,
    frame: impersonationFrame,
    error: impersonationError,
    clearError: clearImpersonationError,
  } = useAccessInvestor({ onClose: returnToView });

  const isMenuOpen = Boolean(anchorEl);

  const [deleteInvestor, { isLoading: isDeleting }] =
    useDeleteAdvisorInvestorMutation();
  const [triggerGetProfile] = useLazyGetAdvisorInvestorUserProfileQuery();
  const [sendPasswordReset, { isLoading: isSendingPasswordReset }] =
    useSendPasswordResetEmailMutation();

  const hasResetPasswordFeature = useMemo(() => {
    // Module must be enabled (not restricted) and feature must be enabled
    return validateByModule('clients') && validateByFeature('resetpassword');
  }, [validateByModule, validateByFeature]);

  const hiddenActions = useMemo(
    () => new Set(options?.hideActions ?? []),
    [options?.hideActions],
  );

  const closeMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSetViewMode = useCallback(
    (mode: ClientViewMode, investor?: AdvisorInvestor) => {
      if (investor) {
        setSelectedInvestor(investor);
      }
      setViewModeState(mode);
      if (mode !== 'accessInvestor') {
        resetAccess();
      }
    },
    [resetAccess],
  );

  useEffect(() => {
    if (viewModeState === 'accessInvestor' && selectedInvestor) {
      startAccess(selectedInvestor.InvestorId);
    }
  }, [viewModeState, selectedInvestor, startAccess]);

  const handleOpenMenu = useCallback(
    (investor: AdvisorInvestor, event: React.MouseEvent<HTMLElement>) => {
      setSelectedInvestor(investor);
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const openMenuForInvestor = useCallback(
    (investor: AdvisorInvestor, event: React.MouseEvent<HTMLElement>) => {
      setSelectedInvestor(investor);
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  // Keep for future use if needed
  // const restrictionHasValue = useCallback(
  //   (type: string, value: string) =>
  //     (appUserInfo?.Restrictions || []).some(
  //       (r) => (r.Type || "").toLowerCase() === type && (r.Value || "").toLowerCase() === value
  //     ),
  //   [appUserInfo?.Restrictions]
  // );

  // Helper to replicate legacy conditions for showing View User Profile
  type AdvisorInvestorWithLegacyFields = AdvisorInvestor & {
    EstateId?: number | string | null;
    InvestorHHId?: number | string | null;
  };
  const canShowViewUserProfile = useCallback(
    (inv: AdvisorInvestorWithLegacyFields): boolean => {
      const role = (currentRole || '').toLowerCase();
      if (role !== 'advisor') return false;
      const hasEstateId = Boolean(inv.EstateId);
      const hasInvestorHHId = Boolean(inv.InvestorHHId);
      if (hasEstateId || hasInvestorHHId) return false;
      return validateByModule('clients') && validateByFeature('userprofile');
    },
    [currentRole, validateByModule, validateByFeature],
  );

  const handleExportProfile = useCallback(
    async (investor: AdvisorInvestor) => {
      try {
        const res = await triggerGetProfile(investor.InvestorId, true).unwrap();
        await exportUserProfilePdf(res, t);
      } catch (err) {
        console.error('Failed to export profile', err);
      }
    },
    [t, triggerGetProfile],
  );

  const MenuForInvestor: React.FC<MenuForInvestorProps> = useCallback(
    ({ investor, className, size, hideTrigger }) => {
      const role = (currentRole || '').toLowerCase();
      const canInviteInvestorByRole = (() => {
        if (role === 'advisor' || role === 'business banker') {
          return (
            validateByModule('AdvisorSetup') &&
            validateByCode('CanInviteClients')
          );
        }
        if (role === 'firm' || role === 'commercial firm') {
          return (
            validateByModule('AdvisorSetup') &&
            validateByCode('CanSeeClientInformation')
          );
        }
        return false;
      })();

      type MenuItemDef = {
        key: ClientActionKey;
        label: string;
        isVisible: boolean;
      };

      const items: MenuItemDef[] = [
        {
          key: 'inviteUser',
          label: t('inviteUser', 'Invite user'),
          // Visible only for Not Invited / Invited clients (steps 1-2) with the
          // API CanInvite flag; hidden for Accepted/Registered. Shares the same
          // eligibility rule as bulk Invite. Still requires role permission.
          isVisible: isInviteEligible(investor) && canInviteInvestorByRole,
        },
        {
          key: 'invitationHistory',
          label: t('invitationHistory', 'Invitation history'),
          // Visible for any Not Invited / Invited client (onboarding status
          // only — not CanInvite), so a Not Invited client still surfaces the
          // "This user has not been invited yet." empty state.
          isVisible: isInvitationHistoryEligible(investor),
        },
        {
          key: 'editUserInformation',
          label: t('editUserInformation', 'Edit user information'),
          isVisible: true,
        },
        {
          key: 'addSpouseOrPartner',
          label: t('addSpouseOrPartner', 'Add spouse/partner'),
          isVisible: true,
        },
        {
          key: 'deleteUser',
          label: t('deleteUser', 'Delete user'),
          // Visible only for Not Invited / Invited clients (steps 1-2) with the
          // API CanDelete flag; hidden for Accepted/Registered.
          isVisible: isDeleteEligible(investor),
        },
        {
          key: 'viewUserProfile',
          label: t('viewUserProfile', 'View user profile'),
          isVisible: canShowViewUserProfile(investor),
        },
        {
          key: 'exportUserProfile',
          label: t('exportUserProfile', 'Export user profile'),
          isVisible: canShowViewUserProfile(investor),
        },
        {
          key: 'sendPasswordReset',
          label: t('sendPasswordResetLink', 'Send Password Reset Link'),
          isVisible:
            typeof investor.UserId === 'number' &&
            Number.isFinite(investor.UserId) &&
            investor.OnboardingStep === OnboardingStepNumber.ACTIVE &&
            hasResetPasswordFeature,
        },
        {
          key: 'accessInvestor',
          label: t('accessInvestor', 'Access Investor'),
          isVisible: investor.CanImpersonate,
        },
      ];

      const visibleItems: MenuItemDef[] = items.filter(
        (it) => it.isVisible && !hiddenActions.has(it.key),
      );

      const idsEqual =
        String(selectedInvestor?.InvestorId ?? '') ===
        String(investor.InvestorId);
      const openForThisInvestor = isMenuOpen && idsEqual;

      const handleConfirmDelete = async () => {
        try {
          await deleteInvestor({
            userId: investor.UserId,
            investorId: investor.InvestorId,
          }).unwrap();
          setDeleteDialogOpen(false);
          options?.onDeleted?.(investor);
        } catch {
          setDeleteDialogOpen(false);
        }
      };

      const handleConfirmPasswordReset = async () => {
        try {
          const body = {
            CanMGPSSO: investor.CanMGPSSO,
            InvestorId: investor.InvestorId,
            Name: investor.Name,
            NetWorth: investor.NetWorth,
            Home: investor.Home,
            HomePercentage: investor.HomePercentage,
            LastLogin: investor.LastLogin ?? null,
            OnboardingStep: investor.OnboardingStep,
            OnboardingStepDisplay: investor.OnboardingStepDisplay,
            CanInvite: investor.CanInvite,

            Groups: investor.Groups ?? [],
            CanImpersonate: investor.CanImpersonate,
            CanDelete: investor.CanDelete,
            InHousehold: investor.InHousehold,
            UserId: investor.UserId,
            UserName: investor.UserName,
            PrimaryAdvisorId: investor.PrimaryAdvisorId ?? null,
            PrimaryAdvisorName: investor.PrimaryAdvisorName ?? null,
            IsAdminLocked: investor.IsAdminLocked,
            IsSensitiveUser: investor.IsSensitiveUser,
          };
          await sendPasswordReset({
            userId: investor.UserId,
            body,
          }).unwrap();
          setPasswordResetDialogOpen(false);
        } catch (err) {
          // TODO(IU-276): replace console logging with a user-facing error state.
          console.error('Failed to send password reset link', err);
          setPasswordResetDialogOpen(false);
        }
      };

      return (
        <>
          {!hideTrigger ? (
            <IconButtonStyledOutlined
              aria-label={t('moreOptions', 'More options')}
              aria-controls={
                openForThisInvestor ? 'client-actions-menu' : undefined
              }
              aria-haspopup='true'
              aria-expanded={openForThisInvestor ? 'true' : undefined}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                handleOpenMenu(investor, e);
              }}
              className={className}
              size={size ?? (isMobile ? 'small' : 'medium')}
            >
              <MoreVertIcon />
            </IconButtonStyledOutlined>
          ) : null}

          <Menu
            id='client-actions-menu'
            anchorEl={anchorEl}
            open={openForThisInvestor}
            onClose={closeMenu}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {visibleItems.map((item, index) => {
              const isDelete = item.key === 'deleteUser';
              const isSendPasswordReset = item.key === 'sendPasswordReset';
              const isFirst = index === 0;
              const isLast = index === visibleItems.length - 1;
              const showDividerBefore =
                (!isFirst && isDelete) || (!isFirst && isSendPasswordReset);
              const showDividerAfter = isDelete && !isLast;
              const isDisabled = item.key === 'addSpouseOrPartner';
              return (
                <React.Fragment key={item.key}>
                  {showDividerBefore ? <Divider /> : null}
                  {(() => {
                    const menuItem = (
                      <MenuItem
                        disabled={isDisabled}
                        onClick={() => {
                          if (isDisabled) return;
                          closeMenu();
                          if (item.key === 'accessInvestor') {
                            handleSetViewMode('accessInvestor', investor);
                            return;
                          }
                          if (item.key === 'editUserInformation') {
                            if (options?.onEditUser) {
                              options.onEditUser(investor);
                            } else {
                              handleSetViewMode('edit', investor);
                            }
                          }
                          if (item.key === 'invitationHistory') {
                            if (
                              (options?.invitationHistoryVariant ??
                                'inline') === 'dialog'
                            ) {
                              setInviteHistoryDialogOpen(true);
                            } else {
                              handleSetViewMode('inviteHistory', investor);
                            }
                          }
                          if (item.key === 'sendPasswordReset') {
                            // Guard with same conditions as legacy
                            const hasUserId =
                              typeof investor.UserId === 'number' &&
                              Number.isFinite(investor.UserId);
                            const isActive =
                              investor.OnboardingStep ===
                              OnboardingStepNumber.ACTIVE;
                            if (
                              !hasUserId ||
                              !isActive ||
                              !hasResetPasswordFeature
                            )
                              return;
                            // Open confirmation modal; payload will be sent on confirm
                            setSelectedInvestor(investor);
                            setPasswordResetDialogOpen(true);
                          }
                          if (item.key === 'deleteUser')
                            setDeleteDialogOpen(true);
                          if (item.key === 'inviteUser')
                            setInviteModalOpen(true);
                          if (item.key === 'addSpouseOrPartner')
                            setAddSpouseOpen(true);
                          if (item.key === 'exportUserProfile')
                            void handleExportProfile(investor);
                          if (item.key === 'viewUserProfile')
                            setViewProfileOpen(true);
                        }}
                        sx={{
                          transition: (theme) =>
                            theme.transitions.create(
                              ['background-color', 'padding'],
                              {
                                duration: theme.transitions.duration.shortest,
                              },
                            ),
                          '&:hover': { backgroundColor: 'action.hover' },
                          ...(isDelete
                            ? { color: (theme) => theme.palette.error.main }
                            : {}),
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    );
                    return isDisabled ? (
                      <Tooltip
                        title={t(
                          'demoFeatureUnavailable',
                          "This feature isn't available in the demo. It's planned for a later release.",
                        )}
                        placement='right'
                      >
                        <span>{menuItem}</span>
                      </Tooltip>
                    ) : (
                      menuItem
                    );
                  })()}
                  {showDividerAfter ? <Divider /> : null}
                </React.Fragment>
              );
            })}
          </Menu>

          {/* Delete confirmation dialog */}
          <ConfirmationDialog
            open={
              deleteDialogOpen &&
              selectedInvestor?.InvestorId === investor.InvestorId
            }
            onClose={() => {
              setDeleteDialogOpen(false);
            }}
            onConfirm={() => {
              void handleConfirmDelete();
            }}
            title={t('areYouSure', 'Are you sure?')}
            contentText={
              <Typography color='error.main'>
                {t(
                  'deleteUserConfirm',
                  'Are you sure you want to delete {{name}}?',
                  { name: investor.Name },
                )}
              </Typography>
            }
            confirmButtonText={t('ok', 'OK')}
            cancelButtonText={t('cancel', 'Cancel')}
            confirmButtonColor='error'
            isLoading={isDeleting}
          />

          {/* Send Password Reset confirmation dialog */}
          <ConfirmationDialog
            open={
              passwordResetDialogOpen &&
              selectedInvestor?.InvestorId === investor.InvestorId
            }
            onClose={() => {
              setPasswordResetDialogOpen(false);
            }}
            onConfirm={() => {
              void handleConfirmPasswordReset();
            }}
            title={t(
              'sendPasswordResetQuestionTitle',
              'Send Password Reset Link?',
            )}
            contentText={t(
              'sendPasswordResetMessage',
              'This will send the user an email with a link to reset their password. Their current password will remain valid until they complete the reset.',
            )}
            confirmButtonText={t('sendResetLink', 'Send Reset Link')}
            cancelButtonText={t('cancel', 'Cancel')}
            isLoading={isSendingPasswordReset}
          />

          {/* Invite one user modal (reuses BulkInviteModal) */}
          <React.Suspense fallback={<LoadingSpinner />}>
            {inviteModalOpen &&
            selectedInvestor?.InvestorId === investor.InvestorId ? (
              <ClientInviteModal
                open={inviteModalOpen}
                onClose={() => {
                  setInviteModalOpen(false);
                }}
                selectedCount={1}
                selectedInvestorIds={[investor.InvestorId]}
                selectedUserIds={[investor.UserId]}
              />
            ) : null}
          </React.Suspense>

          {/* Invitation History dialog variant */}
          <React.Suspense fallback={<LoadingSpinner />}>
            {inviteHistoryDialogOpen &&
            selectedInvestor?.InvestorId === investor.InvestorId &&
            (options?.invitationHistoryVariant ?? 'inline') === 'dialog' ? (
              <Dialog
                open={inviteHistoryDialogOpen}
                onClose={() => {
                  setInviteHistoryDialogOpen(false);
                }}
                fullScreen={isMobile}
                fullWidth
                maxWidth='sm'
              >
                <DialogTitle sx={{ m: 0, p: 2 }}>
                  {t('invitationHistoryTitleFor', {
                    defaultValue: 'Invitation History for {{name}}',
                    name: investor.Name,
                  })}
                  <IconButton
                    aria-label={t('close', 'Close')}
                    onClick={() => {
                      setInviteHistoryDialogOpen(false);
                    }}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                  <InvitationHistory
                    investorId={investor.InvestorId}
                    investorName={investor.Name}
                    onBack={() => {
                      setInviteHistoryDialogOpen(false);
                    }}
                    showBackButton={false}
                    // DialogTitle above already shows the heading; suppress the
                    // component's own title so it doesn't render twice.
                    showTitle={false}
                  />
                </DialogContent>
              </Dialog>
            ) : null}
          </React.Suspense>

          {/* Add Spouse/Partner modal */}
          <React.Suspense fallback={<LoadingSpinner />}>
            {addSpouseOpen &&
            selectedInvestor?.InvestorId === investor.InvestorId ? (
              <AddSpousePartnerModal
                open={addSpouseOpen}
                onClose={() => {
                  setAddSpouseOpen(false);
                }}
                primaryInvestorId={investor.InvestorId}
                primaryInvestorName={investor.Name}
              />
            ) : null}
          </React.Suspense>

          <React.Suspense fallback={<LoadingSpinner />}>
            {viewProfileOpen &&
            selectedInvestor?.InvestorId === investor.InvestorId ? (
              <ViewUserProfileModal
                open={viewProfileOpen}
                onClose={() => {
                  setViewProfileOpen(false);
                }}
                investorId={investor.InvestorId}
                investorName={investor.Name}
              />
            ) : null}
          </React.Suspense>
        </>
      );
    },
    [
      t,
      isMobile,
      isMenuOpen,
      selectedInvestor,
      closeMenu,
      handleOpenMenu,
      deleteDialogOpen,
      isDeleting,
      passwordResetDialogOpen,
      isSendingPasswordReset,
      inviteModalOpen,
      inviteHistoryDialogOpen,
      deleteInvestor,
      options,
      handleExportProfile,
      addSpouseOpen,
      viewProfileOpen,
      hiddenActions,
      handleSetViewMode,
      anchorEl,
      currentRole,
      hasResetPasswordFeature,
      sendPasswordReset,
      validateByModule,
      validateByCode,
      canShowViewUserProfile,
    ],
  );

  const Accompaniment = useMemo(() => {
    if (!selectedInvestor) return null;
    if (viewModeState === 'edit') {
      return (
        <>
          <Box sx={{ px: 1 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                handleSetViewMode('view');
              }}
            >
              {t('back', 'Back')}
            </Button>
          </Box>
          <React.Suspense fallback={<LoadingSpinner />}>
            <EditInvestorFormFlyout
              userId={selectedInvestor.UserId}
              onClose={() => {
                handleSetViewMode('view');
              }}
            />
          </React.Suspense>
        </>
      );
    }
    if (viewModeState === 'inviteHistory') {
      return (
        <React.Suspense fallback={<LoadingSpinner />}>
          <InvitationHistory
            investorId={selectedInvestor.InvestorId}
            investorName={selectedInvestor.Name}
            onBack={() => {
              handleSetViewMode('view');
            }}
          />
        </React.Suspense>
      );
    }
    if (viewModeState === 'accessInvestor') {
      return impersonationFrame;
    }
    return null;
  }, [
    selectedInvestor,
    viewModeState,
    handleSetViewMode,
    t,
    impersonationFrame,
  ]);

  return {
    MenuForInvestor,
    viewMode: viewModeState,
    Accompaniment,
    setViewMode: handleSetViewMode,
    openMenuForInvestor,
    selectedInvestor,
    impersonationError,
    clearImpersonationError,
  };
};

export default useClientActionsMenu;
